import { useCallback, useEffect, useState } from 'react'
import { bettingApi } from '../../api'
import { ErrorState, errorMessage } from '../ErrorState'
import type { AggregatorCandidate, PaymentAggregator, PaymentTestDetail } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { navigate } from '../../shell/router'
import { PageHeader, ViewTabs } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import { aggregatorStatusLabel, FixtureBadge, FixtureToggle, countryLabel, useBettingVocabulary } from './bettingShared'

type CandidateStatus = 'PENDING' | 'LINKED' | 'IGNORED'

/**
 * File d'apprentissage : bénéficiaires et domaines vus pendant les tests que le
 * référentiel ne reconnaît pas encore. Chaque décision enrichit le référentiel.
 */
export function BettingQualify({ canManage }: { canManage: boolean }) {
  const t = useT()
  const { country, showFixtures, setShowFixtures } = useShell()
  const [status, setStatus] = useState<CandidateStatus>('PENDING')
  const [items, setItems] = useState<Record<CandidateStatus, AggregatorCandidate[] | null>>({ PENDING: null, LINKED: null, IGNORED: null })
  const [aggregators, setAggregators] = useState<PaymentAggregator[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setError(null)
    Promise.all((['PENDING', 'LINKED', 'IGNORED'] as const).map((s) => bettingApi.listCandidates(s, showFixtures)))
      .then(([pending, linked, ignored]) => setItems({ PENDING: pending, LINKED: linked, IGNORED: ignored }))
      .catch((err) => setError(errorMessage(err)))
    bettingApi.listAggregators().then(setAggregators).catch(() => undefined)
  }, [showFixtures])
  useEffect(load, [load])

  const inCountry = (list: AggregatorCandidate[] | null) => (list ?? []).filter((c) => !country || c.country === country)
  const shown = inCountry(items[status])

  return (
    <>
      <PageHeader
        title={t('bet.qualifyTitle')}
        description={t('bet.qualifyDesc')}
        actions={<FixtureToggle checked={showFixtures} onChange={setShowFixtures} />}
      />

      <ViewTabs
        label={t('bet.candidateStatus')}
        value={status}
        onChange={setStatus}
        options={[
          { value: 'PENDING', label: t('bet.candPending'), count: items.PENDING ? inCountry(items.PENDING).length : undefined },
          { value: 'LINKED', label: t('bet.candLinked'), count: items.LINKED ? inCountry(items.LINKED).length : undefined },
          { value: 'IGNORED', label: t('bet.candIgnored'), count: items.IGNORED ? inCountry(items.IGNORED).length : undefined },
        ]}
      />

      {error && <ErrorState message={error} onRetry={load} />}
      {!items[status] ? (
        <div className="stat-skeleton" style={{ minHeight: 180 }} />
      ) : shown.length === 0 ? (
        <div className="block empty-note">
          {status === 'PENDING' ? t('bet.qualifyEmptyPending') : t('bet.qualifyEmpty')}
        </div>
      ) : (
        <div className="bet-candidates">
          {shown.map((c) => (
            <CandidateCard key={c.id} candidate={c} aggregators={aggregators} canDecide={canManage && status === 'PENDING'} onDecided={load} />
          ))}
        </div>
      )}
    </>
  )
}

function CandidateCard({
  candidate,
  aggregators,
  canDecide,
  onDecided,
}: {
  candidate: AggregatorCandidate
  aggregators: PaymentAggregator[]
  canDecide: boolean
  onDecided: () => void
}) {
  const { t, fmt } = useI18n()
  const vocab = useBettingVocabulary()
  const [firstTest, setFirstTest] = useState<PaymentTestDetail | null>(null)
  const [choice, setChoice] = useState('')
  const [newName, setNewName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (candidate.firstTestId) bettingApi.getTest(candidate.firstTestId).then(setFirstTest).catch(() => setFirstTest(null))
  }, [candidate.firstTestId])

  const decide = async (decision: { aggregatorId?: string; ignore?: boolean }) => {
    setBusy(true)
    setError(null)
    try {
      await bettingApi.qualifyCandidate(candidate.id, decision)
      onDecided()
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  /** Crée l'agrégateur à partir de l'élément, puis y rattache l'élément. */
  const createAndLink = async () => {
    const name = newName ?? ''
    if (!name.trim()) return
    setBusy(true)
    setError(null)
    try {
      const created = await bettingApi.createAggregator({ name: name.trim(), status: 'UNKNOWN', countries: [candidate.country] })
      await bettingApi.qualifyCandidate(candidate.id, { aggregatorId: created.id })
      onDecided()
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  const context =
    candidate.kind === 'BENEFICIARY'
      ? firstTest?.smsText
      : firstTest
        ? t('bet.candContext', {
            n: firstTest.testNumber,
            service: firstTest.service?.name ?? t('bet.candServiceFallback'),
          })
        : null

  return (
    <article className="block bet-candidate">
      <div className="bet-candidate-main">
        <div className="bet-candidate-tags">
          <span className="status-pill">{candidate.kind === 'BENEFICIARY' ? t('bet.candBeneficiary') : t('bet.candDomain')}</span>
          <span className="block-sub">{countryLabel(vocab, candidate.country, t)}</span>
        </div>
        <span className="bet-candidate-value">{candidate.value}<FixtureBadge when={candidate.isFixture} /></span>
        <div className="bet-candidate-meta">
          <span>{t('bet.candSeenTimes', { n: fmt.number(candidate.occurrences) })}</span>
          {firstTest?.service && <span>{t('bet.candService', { name: firstTest.service.name })}</span>}
          {candidate.firstTestId && (
            <button type="button" className="block-link" onClick={() => navigate(`betting/tests/${candidate.firstTestId}`)}>
              {t('bet.candFirstTest')}
            </button>
          )}
        </div>
        {context && <div className="bet-sms-quote">{context}</div>}
      </div>

      {canDecide && (
        <div className="bet-candidate-decision">
          <span className="panel-section-title">{t('bet.candDecision')}</span>
          <div className="form-stack">
            <select aria-label={t('bet.candAggregatorFor', { value: candidate.value })} value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="">{t('bet.candLinkTo')}</option>
              {aggregators.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({aggregatorStatusLabel(t, a.status)})</option>
              ))}
            </select>
          </div>
          <div className="bet-candidate-buttons">
            <button type="button" className="btn btn-primary" disabled={!choice || busy} onClick={() => decide({ aggregatorId: choice })}>
              {t('bet.candLink')}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setNewName(newName === null ? candidate.value : null)}>
              {t('bet.candNewAggregator')}
            </button>
          </div>
          {newName !== null && (
            <div className="form-stack">
              <label>
                {t('bet.candNewName')}
                <input value={newName} onChange={(e) => setNewName(e.target.value)} />
              </label>
              <button type="button" className="btn btn-primary" disabled={busy || !newName.trim()} onClick={createAndLink}>
                {t('bet.candCreateLink')}
              </button>
            </div>
          )}
          <button type="button" className="block-link bet-candidate-ignore" disabled={busy} onClick={() => decide({ ignore: true })}>
            {t('bet.candIgnore')}
          </button>
          {error && <ErrorState message={error} compact />}
        </div>
      )}
    </article>
  )
}
